import { type ReactNode, useEffect } from "react";
import { Toaster as SonnerToaster, toast as sonner } from "sonner";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export type ToastTone = "info" | "success" | "error";

const EDGE: Record<ToastTone, string> = {
	info: "bg-ink",
	success: "bg-brand",
	error: "bg-danger",
};

interface ToastBaseProps {
	tone?: ToastTone;
	title: ReactNode;
	description?: ReactNode;
	action?: ReactNode;
	className?: string;
}

/**
 * Крестик и его подпись — одно решение, а не два пропа.
 *
 * Союз, а не `closeLabel?: string`: текстового умолчания у кита нет (аудит В1 —
 * русское «Закрыть» уезжало в английский интерфейс), поэтому «крестик есть, а
 * подписи нет» должно ломать типы, а не выдавать кнопку без доступного имени.
 */
export type ToastProps = ToastBaseProps &
	({ onClose: () => void; closeLabel: string } | { onClose?: undefined; closeLabel?: undefined });

/**
 * Белая r12 плашка с --shadow-float и цветной полосой 4px слева.
 *
 * Полоса — единственный носитель тона: заливать весь тост зелёным или красным
 * нельзя, зелёный в системе означает действие, а не «получилось».
 * Заголовок набран Geist, а не Geist Mono, как в исходном DS-компоненте:
 * в IEC ID моноширинное лицо оставлено числам и кодам, а тост — это проза.
 */
export function Toast({
	tone = "info",
	title,
	description,
	action,
	onClose,
	closeLabel,
	className,
}: ToastProps) {
	return (
		<div
			role="status"
			className={cn(
				"relative flex w-full min-w-[280px] max-w-[420px] items-start gap-2.5 overflow-hidden",
				"rounded-card bg-card py-3.5 pr-4 pl-5 shadow-float",
				className,
			)}
		>
			<span aria-hidden="true" className={cn("absolute inset-y-0 left-0 w-1", EDGE[tone])} />
			<div className="flex min-w-0 flex-grow flex-col gap-0.5">
				<span className="t-body">{title}</span>
				{description != null ? <span className="t-meta text-fg-muted">{description}</span> : null}
			</div>
			{action}
			{onClose && closeLabel ? (
				<button
					type="button"
					aria-label={closeLabel}
					onClick={onClose}
					className={cn(
						"iec-press-glyph tap-target inline-flex shrink-0 text-fg-muted transition-colors duration-150 ease-smooth",
						"hover:text-fg",
						"focus-ring",
					)}
				>
					<Icon name="X" size={16} />
				</button>
			) : null}
		</div>
	);
}

export interface ToasterProps {
	position?: "top-right" | "top-center" | "bottom-right" | "bottom-center";
	/**
	 * Подпись крестика для всех тостов приложения — обязательна.
	 *
	 * `notify.*` зовут не из React-дерева (обработчик мутации, перехватчик
	 * ошибки), и до `t()` там не дотянуться. Поэтому подпись регистрирует
	 * `Toaster`: он живёт внутри провайдера i18n и перерисовывается при смене
	 * языка, значит регистрация переживает переключение локали сама.
	 */
	closeLabel: string;
	className?: string;
}

/** Текущая подпись крестика, зарегистрированная `Toaster`. */
let registeredCloseLabel: string | null = null;

/**
 * Точка вывода тостов. Sonner отдаёт очередь, стек и таймеры, а рисуем мы —
 * поэтому `toastOptions.unstyled`: иначе поверх нашей плашки лягут его тени и радиусы.
 */
export function Toaster({ position = "bottom-right", closeLabel, className }: ToasterProps) {
	useEffect(() => {
		registeredCloseLabel = closeLabel;
		return () => {
			if (registeredCloseLabel === closeLabel) registeredCloseLabel = null;
		};
	}, [closeLabel]);

	return (
		<SonnerToaster
			position={position}
			gap={8}
			toastOptions={{ unstyled: true, classNames: { toast: "w-full" } }}
			className={className}
		/>
	);
}

/** Только для тестов: подпись, зарегистрированная текущим `Toaster`. */
export function __getRegisteredCloseLabel(): string | null {
	return registeredCloseLabel;
}

export interface NotifyOptions {
	description?: ReactNode;
	action?: ReactNode;
	duration?: number;
	closable?: boolean;
	/** Перекрывает подпись, зарегистрированную `Toaster`. */
	closeLabel?: string;
}

function show(tone: ToastTone, title: ReactNode, options: NotifyOptions = {}) {
	const { description, action, duration, closable = true } = options;
	// Крестик рисуется только с подписью: доступного имени по умолчанию у кита
	// нет, и кнопка «×» без имени хуже, чем тост, который закроется по таймеру.
	const label = options.closeLabel ?? registeredCloseLabel;
	const rest = {
		...(description !== undefined ? { description } : {}),
		...(action !== undefined ? { action } : {}),
	};
	return sonner.custom(
		(id) =>
			closable && label !== null ? (
				<Toast
					tone={tone}
					title={title}
					{...rest}
					onClose={() => {
						sonner.dismiss(id);
					}}
					closeLabel={label}
				/>
			) : (
				<Toast tone={tone} title={title} {...rest} />
			),
		duration !== undefined ? { duration } : undefined,
	);
}

/**
 * Тонкий фасад над sonner. Приложения зовут `notify.success(...)`, а не сам
 * sonner: так тон, геометрия и подпись кнопки закрытия остаются в одном месте,
 * а замена очереди не тронет вызовы.
 */
export const notify = {
	info: (title: ReactNode, options?: NotifyOptions) => show("info", title, options),
	success: (title: ReactNode, options?: NotifyOptions) => show("success", title, options),
	error: (title: ReactNode, options?: NotifyOptions) => show("error", title, options),
	dismiss: (id?: string | number) => sonner.dismiss(id),
};
